/* eslint-disable @typescript-eslint/no-unused-vars */
/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// React
import {
	useEffect,
	useLayoutEffect,
	useRef,
	useState,
} from 'react';

// Store
import useStore from '@store/index';

// Types
import {
	IApiError,
	TSelectOption,
	TSortColumn,
} from '@global-types/index';

// Hooks
import { useElementSize } from '@mantine/hooks';
import dayjs from 'dayjs';
import { TMeterPoint, TParsedMeterPoint } from '../types';
import { UseMutateFunction } from '@tanstack/react-query';
import { useAuthorization } from '@lib/authorization';
import { ACLS } from '@config/constants/acls';
import { ROLES } from '@config/constants/roles';

/* -------------------------------------------------------------------------- */
/*                                  Hook                                      */
/* -------------------------------------------------------------------------- */

type ObjWithStartDate = {
	startDate: {
		$gte: string;
		$lt: string;
	};
};

/**
 * Custom hook for managing conversion factor list functionality.
 * @returns {Array} An array containing references and callback functions.
 */
export const useMeterPoint = () => {
	const [queryParamsProducers, setQueryParamsProducers] =
		useState({ entity: [] });
	const [queryParamsProjects, setQueryParamsProjects] =
		useState({});
	const [resetFilterSites, setResetFilterSites] =
		useState(false);
	const [resetFilterProjects, setResetFilterProjects] =
		useState(false);
	const { checkAcl, checkAccess } = useAuthorization();
	const [isOverflowFilters] = useState(false);

	const [openDrawer, setOpenDrawer] = useState(false);

	const [
		openUpdateMeterpointDrawer,
		setOpenUpdateMeterpointDrawer,
	] = useState(false);

	const [
		openSelectedItemsToVerifyModal,
		setOpenSelectedItemsToVerifyModal,
	] = useState(false);

	const [
		openSelectedItemsToDownloadModal,
		setOpenSelectedItemsToDownloadModal,
	] = useState(false);

	const [
		openSelectedItemsToDeleteModal,
		setOpenSelectedItemsToDeleteModal,
	] = useState(false);

	const [showScrollFilters, setShowScrollFilters] =
		useState(false);

	const {
		meterPointsPage,
		meterPointsPageSize,
		setMeterPointsPage,
		meterPointsSelectedRecords,
		setMeterPointsSelectedRecords,
		meterPointsOpenConfirmationModalDelete,
		setMeterPointsOpenConfirmationModalDelete,
		meterPointsQueryParams,
		setMeterPointsQueryParams,
		meterPointsSortStatus,
		setMeterPointsSortStatus,
		setMeterPointsSortParams,
		meterPointsSortParams,
		setMeterPointsPageSize,
		setMeterPointsSelectedRow,
		meterPointsSelectedRow,
		setIsOpenStartMint,
		setOpenDrawerMeterPointForm,
		openDrawerMeterPointForm,
		meterpointsDatatableOptions,
		setMeterpointsDatatableOptions,
	} = useStore();

	const containerRef = useRef<HTMLDivElement>(null);

	const handleScroll = (scrollTo: string) => {
		if (containerRef.current) {
			if (scrollTo === 'right') {
				containerRef.current.scrollLeft =
					containerRef.current.scrollLeft +
					containerRef.current.offsetWidth;
			} else {
				containerRef.current.scrollLeft =
					containerRef.current.scrollLeft -
					containerRef.current.offsetWidth;
			}
		}
	};

	const handleSortParams = (_column: TSortColumn) => {
		let sortParams = _column.columnAccessor;
		switch (_column.columnAccessor) {
			case 'place':
				sortParams = 'location.place';
				break;
			case 'mintStatus':
				sortParams = 'mintStatus';
				break;
			default:
				// eslint-disable-next-line @typescript-eslint/no-unused-expressions
				sortParams;
				break;
		}

		if (_column.direction == 'asc') {
			setMeterPointsSortParams(sortParams);
		} else {
			setMeterPointsSortParams('-' + sortParams);
		}
	};

	const handleStartMint = () => {
		//setMeterPointsStartMintsActiveStep(0);
		setIsOpenStartMint(true);
	};

	const handleReset = () => {
		setMeterPointsPageSize(10);
		setMeterPointsQueryParams({});
		setMeterPointsPage(1);
		setMeterPointsSortParams('-createdAt');
		setMeterPointsSortStatus({});
	};

	useEffect(() => {
		return () => {
			handleReset();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	function hasStartDate(obj: ObjWithStartDate) {
		if (
			Object.keys(obj).length === 0 &&
			obj.constructor === Object
		) {
			return false;
		}

		if (
			obj.startDate &&
			obj.startDate.$gte !== undefined &&
			obj.startDate.$lt !== undefined
		) {
			return true;
		}

		if (
			obj.startDate &&
			obj.startDate.$gte === undefined &&
			obj.startDate.$lt === undefined
		) {
			return false;
		}

		return false;
	}

	const NBR_SELECTED_ROWS =
		meterPointsSelectedRecords.length;

	const selectedFields = [
		'_id',
		'project.name',
		'site.name',
		'startDate',
		'quantity',
		'verified',
	];

	const { ref, width: widthContainer } = useElementSize();

	useLayoutEffect(() => {
		if (
			containerRef.current &&
			containerRef.current.clientWidth <
				containerRef.current.scrollWidth
		) {
			setShowScrollFilters(true);
		} else {
			setShowScrollFilters(false);
		}
	}, [widthContainer]);

	/**************************** Edit item ****************************/
	const handleeditmeterpoint = (
		row: TParsedMeterPoint
	) => {
		setMeterPointsSelectedRow(row);
		setOpenUpdateMeterpointDrawer(true);
	};

	const handlecloseeditmeterpoint = () =>
		setOpenUpdateMeterpointDrawer(false);

	/**************************** Pagination ****************************/

	const handlePaginationChangeSize = function (
		_size: string | number
	) {
		setMeterPointsPageSize(Number(_size));
		setMeterPointsPage(1);
	};

	const handleChangePagination = (data: number) => {
		setMeterPointsPage(data);
	};

	/**************************** Filter Projects ****************************/

	const handleapplyprojectfilter = (
		projects: TSelectOption[]
	) => {
		setMeterPointsSelectedRecords([]);
		setMeterPointsPage(1);
		const queryParamsProjects = {
			project: projects.map(
				(_project: TSelectOption) => _project.value
			),
			site: [],
		};
		setQueryParamsProjects(queryParamsProjects);
		setMeterPointsQueryParams({
			...meterPointsQueryParams,
			...queryParamsProjects,
		});
		setResetFilterSites(true);
	};

	/**************************** Filter Date ****************************/
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const handleapplydatefilter = (date: any) => {
		setMeterPointsSelectedRecords([]);
		let startDate;
		let endDate;
		if (
			dayjs(date[0].value[0]).isValid() &&
			dayjs(date[0].value[1]).isValid()
		) {
			startDate = date[0].value[0];
			endDate = date[0].value[1];
		} else {
			startDate = dayjs()
				.subtract(364, 'days')
				.toISOString();
			endDate = dayjs().toISOString();
		}

		const queryParamsDate = {
			startDate: {
				$gte: startDate,
				$lt: endDate,
			},
		};

		setMeterPointsQueryParams({
			...meterPointsQueryParams,
			...queryParamsDate,
		});
		setMeterPointsPage(1);
	};
	/**************************** Filter Sites ****************************/

	const handleapplysitefilter = (
		sites: TSelectOption[]
	) => {
		setMeterPointsSelectedRecords([]);
		setMeterPointsPage(1);
		const queryParamsSites = {
			site: sites.map(
				(_site: TSelectOption) => _site.value
			),
		};

		setMeterPointsQueryParams({
			...meterPointsQueryParams,
			...queryParamsSites,
		});
	};

	/**************************** Filter Producers ****************************/

	const handleapplyproducerfilter = (
		producers: TSelectOption[]
	) => {
		setMeterPointsSelectedRecords([]);
		setMeterPointsPage(1);
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const queryParamsProducers: any = {
			entity: producers.map(
				(_producer: TSelectOption) => _producer.value
			),
			site: [],
			projects: [],
		};
		setQueryParamsProducers(queryParamsProducers);
		setMeterPointsQueryParams({
			...meterPointsQueryParams,
			...queryParamsProducers,
		});
		setResetFilterProjects(true);
		setResetFilterSites(true);
	};
	/**************************** Sort ****************************/

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const handlechangesortstatus = (data: any) => {
		handleSortParams(data);
		setMeterPointsSortStatus(data);
	};

	const handleselectedrecordchange = (
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		selectedRecords: any
	) => {
		setMeterPointsSelectedRecords(selectedRecords);
	};

	/**************************** Datatable ****************************/

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const handlerowclicked = (row: any) => {
		if (
			checkAcl(ACLS.METER_POINTS.READ) &&
			checkAccess({
				allowedRoles: [
					ROLES.ADMIN,
					ROLES.SUPERADMIN,
					ROLES.STAFF,
				],
			})
		) {
			setOpenDrawer(true);
			setMeterPointsSelectedRow(row.record);
		}
	};

	/**************************** Show more ****************************/

	const handlerowviewmore = (row: TParsedMeterPoint) => {
		setMeterPointsSelectedRow(row);
		setOpenDrawer(true);
	};

	const handleCloseDrawerShowMoreMeterPoint = () => {
		setOpenDrawer(false);
	};

	/**************************** Delete confirmation modal ****************************/

	const handlerowremoveitem = (
		row: TParsedMeterPoint
	) => {
		setMeterPointsOpenConfirmationModalDelete({
			open: true,
			params: row,
		});
	};

	const handledeleteconfirmationmodalconfirm = (remove: {
		mutate: UseMutateFunction<
			TMeterPoint,
			IApiError,
			string,
			unknown
		>;
		isLoading: boolean;
	}) => {
		setMeterPointsOpenConfirmationModalDelete({
			open: false,
			params: {},
		});
		remove.mutate(
			String(
				meterPointsOpenConfirmationModalDelete.params.id
			)
		);
		setOpenDrawer(false);
	};

	const handleclosedeleteconfirmationmodal = () => {
		setMeterPointsOpenConfirmationModalDelete({
			open: false,
			params: {},
		});
	};

	/**************************** Delete confirmation modal ****************************/

	const handledeleteconversionfactor = () => {
		setMeterPointsOpenConfirmationModalDelete({
			open: true,
			params: meterPointsSelectedRow,
		});
	};

	/**************************** Drawer add conversion factor ****************************/

	const handleCloseDrawerAddMeterPoint = () => {
		setOpenDrawerMeterPointForm(false);
	};

	/********************************************************************************************/

	return {
		/**************************** Drawer add conversion factor ****************************/
		handleCloseDrawerAddMeterPoint,
		/**************************** Delete item ****************************/
		handlerowremoveitem,
		handledeleteconfirmationmodalconfirm,
		handleclosedeleteconfirmationmodal,
		handledeleteconversionfactor,
		/**************************** Show more ****************************/
		handlerowviewmore,
		handleCloseDrawerShowMoreMeterPoint,
		/**************************** Datatable ****************************/
		handlerowclicked,
		/**************************** Select item ****************************/
		handleselectedrecordchange,
		/**************************** Sort ****************************/
		handlechangesortstatus,
		/**************************** Filters ****************************/
		handleapplyprojectfilter,
		handleapplysitefilter,
		handleapplydatefilter,
		handleapplyproducerfilter,
		/**************************** Pagination ****************************/
		handleChangePagination,
		handlePaginationChangeSize,
		meterPointsPage,
		meterPointsPageSize,

		setMeterPointsPage,
		meterPointsSelectedRecords,
		setMeterPointsSelectedRecords,
		meterPointsOpenConfirmationModalDelete,
		setMeterPointsOpenConfirmationModalDelete,
		meterPointsQueryParams,
		setMeterPointsQueryParams,
		meterPointsSortStatus,
		setMeterPointsSortStatus,
		setMeterPointsSortParams,
		meterPointsSortParams,
		setMeterPointsPageSize,
		setMeterPointsSelectedRow,
		meterPointsSelectedRow,
		setIsOpenStartMint,
		queryParamsProducers,
		setQueryParamsProducers,
		queryParamsProjects,
		setQueryParamsProjects,
		handleScroll,
		resetFilterSites,
		setResetFilterSites,
		isOverflowFilters,
		openDrawer,
		setOpenDrawer,
		openSelectedItemsToDownloadModal,
		setOpenSelectedItemsToDownloadModal,
		openSelectedItemsToVerifyModal,
		setOpenSelectedItemsToVerifyModal,
		openSelectedItemsToDeleteModal,
		setOpenSelectedItemsToDeleteModal,
		showScrollFilters,
		setShowScrollFilters,
		openDrawerMeterPointForm,
		setOpenDrawerMeterPointForm,
		handleSortParams,
		handleStartMint,
		hasStartDate,
		NBR_SELECTED_ROWS,
		selectedFields,
		ref,
		containerRef,
		resetFilterProjects,
		setResetFilterProjects,
		meterpointsDatatableOptions,
		setMeterpointsDatatableOptions,
		setOpenUpdateMeterpointDrawer,
		openUpdateMeterpointDrawer,
		handlecloseeditmeterpoint,
		handleeditmeterpoint,
	};
};
